import * as React from "react";
import { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import { useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigPost } from "../getConfig";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
    padding: "3em 2em",
    boxSizing: "border-box",
    width: "100%",
    overflow: "hidden",
  },
  container: {
    width: "100%",
    maxWidth: "700px",
    boxSizing: "border-box",
    padding: "0 1em",
  },
  pageTitle: {
    marginBottom: "2em",
    textAlign: "center",
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "2rem",
  },
  card: {
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    borderRadius: "12px",
    marginBottom: "2em",
  },
  formField: {
    marginBottom: "1.5em",
    "& .MuiInputBase-root": {
      backgroundColor: "white",
    },
  },
  submitButton: {
    padding: "12px 48px",
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(63, 81, 181, 0.3)",
    transition: "all 0.3s ease",
    marginTop: "1em",
    "&:hover": {
      boxShadow: "0 6px 16px rgba(63, 81, 181, 0.4)",
      transform: "translateY(-2px)",
    },
  },
  warningBox: {
    marginBottom: "2em",
    padding: "1.5em",
    backgroundColor: "#fff3e0",
    borderRadius: "8px",
    borderLeft: "4px solid #e65100",
  },
  warningText: {
    color: "#e65100",
    fontSize: "0.95rem",
  },
  resultCard: {
    marginTop: "2em",
    padding: "2em",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(102, 126, 234, 0.4)",
    color: "white",
  },
  resultTitle: {
    color: "rgba(255,255,255,0.9)",
    marginBottom: "1em",
    fontSize: "1.2rem",
    fontWeight: 600,
  },
  resultItem: {
    marginBottom: "1em",
    padding: "0.75em",
    backgroundColor: "rgba(255,255,255,0.1)",
    borderRadius: "8px",
  },
  resultLabel: {
    color: "rgba(255,255,255,0.9)",
    fontSize: "0.9rem",
    marginBottom: "0.25em",
  },
  resultValue: {
    color: "white",
    fontSize: "1.1rem",
    fontWeight: 600,
  },
}));

const ResultField = ({ classes, label, value }: { classes: any; label: string; value: any }) => (
  <div className={classes.resultItem}>
    <Typography className={classes.resultLabel}>{label}</Typography>
    <Typography className={classes.resultValue}>{value ?? "N/A"}</Typography>
  </div>
);

type Destination = "refund" | "expenses";

const DESTINATION_LABEL: Record<Destination, string> = {
  refund: "Refund account",
  expenses: "Expenses account",
};

interface InternalTransferResult {
  success: boolean;
  flag: string;
  destination: Destination;
  amount: number;
  payment_reference: string;
  message_id: string;
  instruction_id: string;
  received_at: string;
}

const formatDate = (d: string | null | undefined): string => {
  if (!d) return "N/A";
  try {
    const date = new Date(d);
    return isNaN(date.getTime()) ? "N/A" : date.toLocaleString();
  } catch {
    return "N/A";
  }
};

const NatwestInternalTransferDashboard = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [destination, setDestination] = useState<Destination>("refund");
  const [amount, setAmount] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<InternalTransferResult | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const validate = () => {
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      notify("Amount must be a positive number", "error");
      return false;
    }
    if (Number(amount) > 150000) {
      notify("Amount exceeds the maximum allowed transfer of £150,000", "error");
      return false;
    }
    return true;
  };

  const handleSend = async () => {
    setConfirmOpen(false);
    try {
      setLoading(true);
      const res = await axios.post<{ data: InternalTransferResult }>(
        ENDPOINTS.NATWEST_TRANSFER,
        {
          flag: "internal_transfer",
          destination,
          amount: Number(amount),
          ...(paymentReference.trim() ? { payment_reference: paymentReference.trim() } : {}),
        },
        { headers: getConfigPost() }
      );
      const data = res.data.data || (res.data as unknown as InternalTransferResult);
      setResult(data);
      notify(`Internal transfer initiated: £${amount} to ${DESTINATION_LABEL[destination]}`, "info");
    } catch (err: any) {
      notify(err.response?.data?.message || "Internal transfer failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setAmount("");
    setPaymentReference("");
    setResult(null);
  };

  return (
    <div className={classes.main}>
      <div className={classes.container}>
        <Typography variant="h4" className={classes.pageTitle}>
          NatWest Internal Transfer
        </Typography>

        <Card className={classes.card}>
          <CardContent>
            <div className={classes.warningBox}>
              <Typography className={classes.warningText}>
                ⚠ ADMIN + TECH only. This moves funds from the Sprive NatWest <strong>Funding</strong> account
                to the selected internal account via NatWest FPS. Both accounts are taken from server config —
                you only choose the destination and amount. Amount in GBP.
              </Typography>
            </div>

            <FormControl component="fieldset" style={{ marginBottom: "1.5em" }}>
              <FormLabel component="legend" style={{ marginBottom: "0.5em", fontWeight: 600 }}>
                Destination account
              </FormLabel>
              <RadioGroup
                row
                value={destination}
                onChange={(e) => setDestination(e.target.value as Destination)}
              >
                <FormControlLabel value="refund" control={<Radio color="primary" />} label="Refund" disabled={loading} />
                <FormControlLabel value="expenses" control={<Radio color="primary" />} label="Expenses" disabled={loading} />
              </RadioGroup>
            </FormControl>

            <TextField
              label="Amount (£)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              variant="outlined"
              className={classes.formField}
              type="number"
              inputProps={{ min: "0.01", step: "0.01" }}
              placeholder="1000"
              disabled={loading}
            />

            <TextField
              label="Payment Reference (optional)"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              fullWidth
              variant="outlined"
              className={classes.formField}
              placeholder="Leave empty for auto-generated"
              disabled={loading}
            />

            <Button
              variant="contained"
              color="primary"
              className={classes.submitButton}
              disabled={loading || !amount || Number(amount) <= 0}
              onClick={() => { if (validate()) setConfirmOpen(true); }}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? "Sending..." : `Send to ${DESTINATION_LABEL[destination]}`}
            </Button>
            {result && (
              <Button
                variant="outlined"
                onClick={handleReset}
                style={{ marginTop: "1em", marginLeft: "1em", textTransform: "none" }}
              >
                Reset
              </Button>
            )}

            {result && (
              <Paper className={classes.resultCard}>
                <Typography className={classes.resultTitle}>✓ Internal Transfer Initiated</Typography>
                <ResultField classes={classes} label="Destination" value={DESTINATION_LABEL[result.destination] || result.destination} />
                <ResultField classes={classes} label="Amount" value={`£${result.amount ?? amount}`} />
                <ResultField classes={classes} label="Payment Reference" value={result.payment_reference} />
                <ResultField classes={classes} label="Message ID" value={result.message_id} />
                <ResultField classes={classes} label="Instruction ID" value={result.instruction_id} />
                <ResultField classes={classes} label="Received At" value={formatDate(result.received_at)} />
              </Paper>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm NatWest Internal Transfer</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to transfer <strong>£{amount}</strong> from the Sprive NatWest{" "}
            <strong>Funding</strong> account to the <strong>{DESTINATION_LABEL[destination]}</strong>.
          </Typography>
          {paymentReference.trim() && (
            <Typography style={{ marginTop: "0.5em" }}>
              Payment Reference: <strong>{paymentReference.trim()}</strong>
            </Typography>
          )}
          <Typography style={{ marginTop: "1em", color: "#c62828" }}>
            This will initiate a real bank transfer. Please confirm.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="default">Cancel</Button>
          <Button onClick={handleSend} color="primary" variant="contained">Confirm &amp; Send</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default NatwestInternalTransferDashboard;
